import React, { useEffect, useState, useRef, useCallback } from "react";
import { Box, Typography, Button, Grid, Card, CardContent, CircularProgress, Modal, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

// Configuration
// Use WSS for production (HTTPS) and WS for local development (HTTP)
const isProduction = window.location.protocol === 'https:';

// Production uses domain with valid Let's Encrypt SSL certificate
const MONITORING_SERVER = isProduction 
  ? "wss://monitor.thobwabaya.com"  // ✅ Valid SSL certificate
  : "ws://152.42.235.70:8082";

const ICE_SERVERS = [
  {
    urls: [
      "turns:monitor.thobwabaya.com:5349?transport=tcp",  // TURNS (TLS) - works through strict firewalls
      "turns:monitor.thobwabaya.com:3478?transport=tcp",  // TURNS (TLS) fallback
      "turn:monitor.thobwabaya.com:3478?transport=tcp",   // Regular TURN fallback
      "turn:monitor.thobwabaya.com:3478?transport=udp"    // UDP fallback
    ],
    username: "aischool",
    credential: "schoolmonitor2024"
  },
  {
    urls: "stun:monitor.thobwabaya.com:3478"
  }
];

/**
 * LiveMonitorTab with WebRTC and TURN server support
 * Connects to monitoring server on Digital Ocean VPS (152.42.235.70)
 */
const LiveMonitorTab_webrtc = ({ teacherId = "teacher@school.edu", formId = null }) => {
  const [activeStudents, setActiveStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]); // All students from database
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [peerConnections, setPeerConnections] = useState({});
  const [videoElements, setVideoElements] = useState({});
  const [autoMonitoring, setAutoMonitoring] = useState({}); // Track which students are being auto-monitored
  const [fullscreenStudent, setFullscreenStudent] = useState(null); // For fullscreen modal
  
  const wsRef = useRef(null);
  const peerConnectionsRef = useRef({});
  const videoRefsRef = useRef({});
  const canvasRefsRef = useRef({});
  const autoMonitoringRef = useRef({});

  // Connect to monitoring server
  useEffect(() => {
    connectToMonitoringServer();
    
    // Note: We don't fetch all students from database anymore
    // We only show students who are actually online and can be monitored
    // This is simpler and faster - offline students can't be monitored anyway
    
    return () => {
      // Cleanup on unmount
      if (wsRef.current) {
        wsRef.current.close();
      }
      // Close all peer connections
      Object.values(peerConnectionsRef.current).forEach(pc => {
        if (pc) pc.close();
      });
    };
  }, [teacherId, formId]);

  // Handle ESC key to close fullscreen
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' && fullscreenStudent) {
        setFullscreenStudent(null);
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [fullscreenStudent]);

  const connectToMonitoringServer = () => {
    console.log('🔗 Connecting to monitoring server...');
    console.log(`📚 Form filter: ${formId || 'All forms'}`);
    console.log(`🌐 Server URL: ${MONITORING_SERVER}/ws/teacher/${encodeURIComponent(teacherId)}`);
    
    try {
      const ws = new WebSocket(`${MONITORING_SERVER}/ws/teacher/${encodeURIComponent(teacherId)}`);

      ws.onopen = () => {
        console.log('✅ Connected to monitoring server');
        setConnectionStatus('connected');
      
      // Request student list with form filter
      ws.send(JSON.stringify({
        type: "request_student_list",
        form_filter: formId  // Send form filter to server
      }));
    };

    ws.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📨 Message from server:', data.type);

        switch (data.type) {
          case 'turn_config':
            console.log('🔄 Received TURN configuration');
            break;

          case 'student_list':
            console.log(`👥 Student list received: ${data.students.length} students`);
            console.log(`📚 Form filter: ${data.form_filter || 'None'}`);
            console.log(`📝 Student IDs:`, data.students.map(s => s.student_id));
            
            // Server already filtered by form, just use the results
            setActiveStudents(data.students);
            
            // Merge with existing students instead of replacing
            // This prevents second student from replacing the first
            setAllStudents(prevStudents => {
              const newStudents = data.students.map(s => ({
                ...s,
                is_online: true
              }));
              
              // Create a map of existing students by ID
              const studentMap = new Map();
              prevStudents.forEach(s => studentMap.set(s.student_id, s));
              
              // Add/update new students
              newStudents.forEach(s => studentMap.set(s.student_id, s));
              
              // Convert back to array
              return Array.from(studentMap.values());
            });
            
            // Auto-start monitoring for all online students
            data.students.forEach(student => {
              // Check both autoMonitoring flag AND peer connection existence
              const alreadyMonitoring = autoMonitoringRef.current[student.student_id];
              const hasConnection = peerConnectionsRef.current[student.student_id];
              
              if (!alreadyMonitoring && !hasConnection) {
                console.log(`🚀 Auto-starting monitoring for ${student.student_id} (from student_list)`);
                autoMonitoringRef.current[student.student_id] = true;
                
                // Start monitoring after a short delay
                setTimeout(() => {
                  startMonitoring(student);
                }, 1000);
              } else {
                console.log(`⏭️ Skipping ${student.student_id} - already monitoring (flag: ${alreadyMonitoring}, connection: ${!!hasConnection})`);
              }
            });
            break;

          case 'student_status':
            console.log(`👨‍🎓 Student ${data.student_id} is ${data.status}`);
            console.log(`📚 Student form: ${data.form}, Current form: ${formId}`);
            
            if (data.status === 'disconnected') {
              // Remove student from all lists and cleanup
              console.log(`🚪 Removing disconnected student ${data.student_id}`);
              
              setAllStudents(prev => prev.filter(s => s.student_id !== data.student_id));
              setActiveStudents(prev => prev.filter(s => s.student_id !== data.student_id));
              
              // Clean up peer connection
              if (peerConnectionsRef.current[data.student_id]) {
                peerConnectionsRef.current[data.student_id].close();
                delete peerConnectionsRef.current[data.student_id];
              }
              
              delete autoMonitoringRef.current[data.student_id];
              break;
            }
            
            // Only update if student belongs to current form or if no form filter
            if (!formId || data.form?.toUpperCase() === formId?.toUpperCase()) {
              // For connected status, immediately add student to UI
              if (data.status === 'connected' || data.status === 'online') {
                console.log(`➕ Adding new student to list: ${data.student_id}`);
                
                const newStudent = {
                  student_id: data.student_id,
                  name: data.student_info?.name || data.name || data.student_id,
                  form: data.form,
                  is_online: true
                };
                
                // Add to both lists immediately
                setAllStudents(prev => {
                  // Don't add if already exists
                  if (prev.some(s => s.student_id === data.student_id)) {
                    return prev;
                  }
                  return [...prev, newStudent];
                });
                
                setActiveStudents(prev => {
                  if (prev.some(s => s.student_id === data.student_id)) {
                    return prev;
                  }
                  return [...prev, newStudent];
                });
                
                // Auto-start monitoring if not already monitoring
                const alreadyMonitoring = autoMonitoringRef.current[data.student_id];
                const hasConnection = peerConnectionsRef.current[data.student_id];
                
                if (!alreadyMonitoring && !hasConnection) {
                  console.log(`🚀 Auto-starting monitoring for ${data.student_id} (from student_status)`);
                  autoMonitoringRef.current[data.student_id] = true;
                  
                  // Start monitoring after a short delay to ensure connection is stable
                  setTimeout(() => {
                    startMonitoring(newStudent);
                  }, 1000);
                } else {
                  console.log(`⏭️ Skipping ${data.student_id} - already monitoring (flag: ${alreadyMonitoring}, connection: ${!!hasConnection})`);
                }
              }
            } else {
              console.log(`⏭️ Skipping student ${data.student_id} - wrong form (${data.form} !== ${formId})`);
            }
            break;

          case 'webrtc_signal':
            await handleWebRTCSignal(data);
            break;

          default:
            console.log('❓ Unknown message type:', data.type);
        }
      } catch (error) {
        console.error('❌ Error processing message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
      console.error('💡 If using HTTPS, you may need to visit https://152.42.235.70 and accept the certificate first');
      setConnectionStatus('error');
    };

    ws.onclose = () => {
      console.log('📡 Disconnected from monitoring server');
      setConnectionStatus('disconnected');
      
      // Attempt to reconnect after 5 seconds
      setTimeout(() => {
        console.log('🔄 Attempting to reconnect...');
        connectToMonitoringServer();
      }, 5000);
    };

    wsRef.current = ws;
    } catch (error) {
      console.error('❌ Failed to create WebSocket connection:', error);
      console.error('💡 Solution: Visit https://152.42.235.70 in a new tab and accept the SSL certificate');
      setConnectionStatus('error');
    }
  };

  const handleWebRTCSignal = async (signal) => {
    const { from_id, signal_type, data } = signal;
    console.log(`📡 WebRTC signal from ${from_id}: ${signal_type}`);

    if (signal_type === 'offer') {
      // Received offer from student, create answer
      await handleOffer(from_id, data);
    } else if (signal_type === 'answer') {
      // Received answer from student
      await handleAnswer(from_id, data);
    } else if (signal_type === 'ice-candidate') {
      // Received ICE candidate
      await handleIceCandidate(from_id, data);
    }
  };

  const createPeerConnection = (studentId) => {
    console.log(`🔗 Creating peer connection for ${studentId}`);
    
    const pc = new RTCPeerConnection({
      iceServers: ICE_SERVERS,
      iceCandidatePoolSize: 10
    });

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        console.log(`🧊 ICE candidate for ${studentId}`);
        sendSignal(studentId, 'ice-candidate', event.candidate);
      }
    };

    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      console.log(`🔌 Connection state for ${studentId}: ${pc.connectionState}`);
    };

    // Handle ICE connection state
    pc.oniceconnectionstatechange = () => {
      console.log(`🧊 ICE connection state for ${studentId}: ${pc.iceConnectionState}`);
    };

    // Handle incoming stream
    pc.ontrack = (event) => {
      console.log(`📺 Received track from ${studentId}`);
      const [stream] = event.streams;
      
      // Try to attach stream, retry if element not ready
      const tryAttachStream = (retryCount = 0) => {
        const videoId = `video-${studentId}`;
        const videoElement = document.getElementById(videoId);
        
        if (videoElement && !videoElement.srcObject) {
          videoElement.srcObject = stream;
          videoElement.play().catch(e => console.log('Video autoplay prevented:', e));
          console.log(`✅ Stream attached to video element for ${studentId}`);
        } else if (retryCount < 10) {
          // Retry after 100ms if element not ready yet
          setTimeout(() => tryAttachStream(retryCount + 1), 100);
        } else {
          console.warn(`⚠️ Could not find video element for ${studentId} after ${retryCount} retries`);
        }
      };
      
      tryAttachStream();
    };

    peerConnectionsRef.current[studentId] = pc;
    return pc;
  };

  const startMonitoring = async (student) => {
    const studentId = student.student_id;
    console.log(`👁️ Starting monitoring for ${studentId}`);
    
    // CRITICAL: Check if already monitoring to prevent duplicate connections
    if (peerConnectionsRef.current[studentId]) {
      console.warn(`⚠️ Already monitoring ${studentId}, skipping duplicate connection`);
      return;
    }

    // Notify server we're starting to monitor this student
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "start_monitoring",
        student_id: studentId
      }));

      // Create peer connection and send offer
      const pc = createPeerConnection(studentId);
      
      // Create offer
      try {
        const offer = await pc.createOffer({
          offerToReceiveVideo: true,
          offerToReceiveAudio: false
        });
        
        await pc.setLocalDescription(offer);
        
        // Send offer to student via server
        sendSignal(studentId, 'offer', offer);
        
        console.log(`📤 Sent offer to ${studentId}`);
      } catch (error) {
        console.error(`❌ Error creating offer for ${studentId}:`, error);
        // Clean up on error
        if (peerConnectionsRef.current[studentId]) {
          peerConnectionsRef.current[studentId].close();
          delete peerConnectionsRef.current[studentId];
        }
        delete autoMonitoringRef.current[studentId];
      }
    }
  };

  const stopMonitoring = (studentId) => {
    console.log(`⏸️ Stopping monitoring for ${studentId}`);
    
    // Close peer connection
    if (peerConnectionsRef.current[studentId]) {
      peerConnectionsRef.current[studentId].close();
      delete peerConnectionsRef.current[studentId];
    }

    // Notify server
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "stop_monitoring",
        student_id: studentId
      }));
    }
  };

  const sendSignal = (targetId, signalType, data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "webrtc_signal",
        signal_type: signalType,
        target_id: targetId,
        data: data
      }));
    }
  };

  const handleOffer = async (studentId, offer) => {
    console.log(`📥 Handling offer from ${studentId}`);
    
    let pc = peerConnectionsRef.current[studentId];
    
    // If connection exists but is closed, recreate it
    if (pc && pc.signalingState === 'closed') {
      console.log(`🔄 Recreating closed connection for ${studentId}`);
      pc.close();
      delete peerConnectionsRef.current[studentId];
      pc = null;
    }
    
    // Create new connection if needed
    if (!pc) {
      pc = createPeerConnection(studentId);
    }

    try {
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      
      sendSignal(studentId, 'answer', answer);
      console.log(`📤 Sent answer to ${studentId}`);
    } catch (error) {
      console.error(`❌ Error handling offer from ${studentId}:`, error);
    }
  };

  const handleAnswer = async (studentId, answer) => {
    console.log(`📥 Handling answer from ${studentId}`);
    
    const pc = peerConnectionsRef.current[studentId];
    if (!pc) {
      console.warn(`⚠️ No peer connection found for ${studentId}`);
      return;
    }
    
    // Check if connection is closed or in wrong state
    if (pc.signalingState === 'closed') {
      console.warn(`⚠️ Peer connection closed for ${studentId}, ignoring answer`);
      // Clean up and potentially recreate
      delete peerConnectionsRef.current[studentId];
      delete autoMonitoringRef.current[studentId];
      return;
    }
    
    // Only set remote description if we're in the right state
    if (pc.signalingState !== 'have-local-offer') {
      console.warn(`⚠️ Peer connection in wrong state (${pc.signalingState}) for ${studentId}, ignoring answer`);
      return;
    }
    
    try {
      await pc.setRemoteDescription(new RTCSessionDescription(answer));
      console.log(`✅ Set remote description for ${studentId}`);
    } catch (error) {
      console.error(`❌ Error handling answer from ${studentId}:`, error);
      // Clean up on error
      if (pc) {
        pc.close();
        delete peerConnectionsRef.current[studentId];
        delete autoMonitoringRef.current[studentId];
      }
    }
  };

  const handleIceCandidate = async (studentId, candidate) => {
    const pc = peerConnectionsRef.current[studentId];
    if (!pc) {
      console.warn(`⚠️ No peer connection found for ${studentId} ICE candidate`);
      return;
    }
    
    // Check if connection is closed
    if (pc.signalingState === 'closed') {
      console.warn(`⚠️ Peer connection closed for ${studentId}, ignoring ICE candidate`);
      return;
    }
    
    try {
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
      console.log(`🧊 Added ICE candidate for ${studentId}`);
    } catch (error) {
      console.error(`❌ Error adding ICE candidate for ${studentId}:`, error);
    }
  };

  // Remote control functions
  const sendControlCommand = (command, studentIds, data = {}) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return;
    }

    const message = {
      type: "control_command",
      command,
      student_ids: studentIds,
      data
    };

    console.log(`🎮 Sending control command: ${command} to ${studentIds.length} students`);
    wsRef.current.send(JSON.stringify(message));
  };

  const blankScreen = (studentId) => {
    sendControlCommand("blank_screen", [studentId]);
  };

  const unblankScreen = (studentId) => {
    sendControlCommand("unblank_screen", [studentId]);
  };

  const blankAllScreens = () => {
    const onlineStudentIds = activeStudents.map(s => s.student_id);
    sendControlCommand("blank_screen", onlineStudentIds);
  };

  const unblankAllScreens = () => {
    const onlineStudentIds = activeStudents.map(s => s.student_id);
    sendControlCommand("unblank_screen", onlineStudentIds);
  };

  const openUrl = (studentId, url) => {
    sendControlCommand("open_url", [studentId], { url });
  };

  const openUrlAll = (url) => {
    const onlineStudentIds = activeStudents.map(s => s.student_id);
    sendControlCommand("open_url", onlineStudentIds, { url });
  };

  const [urlInput, setUrlInput] = useState("");
  const [showUrlDialog, setShowUrlDialog] = useState(false);
  const [urlTargetStudent, setUrlTargetStudent] = useState(null);

  const handleOpenUrlClick = (studentId = null) => {
    setUrlTargetStudent(studentId);
    setShowUrlDialog(true);
  };

  const handleUrlSubmit = () => {
    if (!urlInput.trim()) return;
    
    let finalUrl = urlInput.trim();
    // Add https:// if no protocol specified
    if (!finalUrl.match(/^https?:\/\//i)) {
      finalUrl = 'https://' + finalUrl;
    }

    if (urlTargetStudent) {
      openUrl(urlTargetStudent, finalUrl);
    } else {
      openUrlAll(finalUrl);
    }

    setUrlInput("");
    setShowUrlDialog(false);
    setUrlTargetStudent(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* SSL Certificate Warning */}
      {connectionStatus === 'error' && (
        <Box sx={{ mb: 3, p: 2, bgcolor: 'error.light', borderRadius: 2, border: '2px solid', borderColor: 'error.main' }}>
          <Typography variant="h6" color="error.dark" gutterBottom>
            ⚠️ Connection Error
          </Typography>
          <Typography variant="body2" color="error.dark">
            Failed to connect to monitoring server. If you're on HTTPS:
          </Typography>
          <Typography variant="body2" color="error.dark" sx={{ mt: 1 }}>
            1. Open <a href="https://152.42.235.70" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>https://152.42.235.70</a> in a new tab
          </Typography>
          <Typography variant="body2" color="error.dark">
            2. Accept the SSL certificate warning (click "Advanced" → "Proceed to site")
          </Typography>
          <Typography variant="body2" color="error.dark">
            3. Return here and refresh the page
          </Typography>
        </Box>
      )}
      
      {/* Control Panel */}
      {activeStudents.length > 0 && (
        <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.light', borderRadius: 2, border: '2px solid', borderColor: 'primary.main' }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.dark' }}>
            🎮 Remote Control Panel
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="warning"
              onClick={blankAllScreens}
            >
              🔒 Blank All Screens
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={unblankAllScreens}
            >
              🔓 Unblank All Screens
            </Button>
            <Button
              variant="contained"
              color="info"
              onClick={() => handleOpenUrlClick(null)}
            >
              🌐 Open URL on All Screens
            </Button>
          </Box>
        </Box>
      )}

      {/* URL Dialog */}
      <Modal
        open={showUrlDialog}
        onClose={() => setShowUrlDialog(false)}
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2
        }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            🌐 Open Website
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            {urlTargetStudent
              ? `Send URL to ${allStudents.find(s => s.student_id === urlTargetStudent)?.name || 'selected student'}`
              : `Send URL to all ${activeStudents.length} online students`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <input
              type="text"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleUrlSubmit()}
              placeholder="Enter URL (e.g., google.com)"
              style={{
                flex: 1,
                padding: '10px',
                fontSize: '16px',
                border: '2px solid #ccc',
                borderRadius: '4px'
              }}
              autoFocus
            />
            <Button
              variant="contained"
              onClick={handleUrlSubmit}
              disabled={!urlInput.trim()}
            >
              Send
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setShowUrlDialog(false);
                setUrlInput("");
                setUrlTargetStudent(null);
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Students grid */}
      {allStudents.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Waiting for students to connect...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Students will appear here when they start the monitoring agent
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {/* Filter students by form before displaying */}
          {allStudents
            .filter(student => !formId || student.form?.toUpperCase() === formId?.toUpperCase())
            .map((student) => {
            const isOnline = activeStudents.some(s => s.student_id === student.student_id);
            const isMonitoring = peerConnectionsRef.current[student.student_id];
            
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={student.student_id}>
                <Card sx={{ 
                  height: '100%',
                  opacity: isOnline ? 1 : 0.6,
                  border: isOnline ? '2px solid #4caf50' : '1px solid #e0e0e0'
                }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                        {student.name || student.student_id}
                      </Typography>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: isOnline ? 'success.main' : 'grey.400',
                          animation: isOnline ? 'pulse 2s infinite' : 'none'
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color={isOnline ? "success.main" : "text.disabled"} gutterBottom display="block" sx={{ mb: 1 }}>
                      {isOnline ? '🟢 Online' : '⚫ Offline'}
                    </Typography>
                    
                    {/* Video element - only show for online students */}
                    {isOnline && (
                      <Box
                        onClick={() => isMonitoring && setFullscreenStudent(student)}
                        sx={{
                          width: '100%',
                          height: 200,
                          bgcolor: 'black',
                          borderRadius: 1,
                          mb: 2,
                          position: 'relative',
                          overflow: 'hidden',
                          cursor: isMonitoring ? 'pointer' : 'default',
                          '&:hover': isMonitoring ? {
                            opacity: 0.9,
                            boxShadow: '0 0 10px rgba(76, 175, 80, 0.5)'
                          } : {}
                        }}
                      >
                        <video
                          id={`video-${student.student_id}`}
                          autoPlay
                          playsInline
                          muted
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain'
                          }}
                        />
                        {!isMonitoring && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              color: 'white',
                              textAlign: 'center'
                            }}
                          >
                            <CircularProgress size={30} sx={{ color: 'white', mb: 1 }} />
                            <Typography variant="caption">Connecting...</Typography>
                          </Box>
                        )}
                        {isMonitoring && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              right: 8,
                              bgcolor: 'rgba(0,0,0,0.6)',
                              color: 'white',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.7rem'
                            }}
                          >
                            🔍 Click to expand
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Placeholder for offline students */}
                    {!isOnline && (
                      <Box
                        sx={{
                          width: '100%',
                          height: 200,
                          bgcolor: 'grey.200',
                          borderRadius: 1,
                          mb: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Typography variant="body2" color="text.disabled">
                          Not Connected
                        </Typography>
                      </Box>
                    )}

                    {/* Only show button for online students if not auto-monitoring */}
                    {isOnline && !isMonitoring && (
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={() => startMonitoring(activeStudents.find(s => s.student_id === student.student_id))}
                        sx={{ py: 0.5, fontSize: '0.75rem' }}
                      >
                        ▶️
                      </Button>
                    )}
                    {isMonitoring && (
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => stopMonitoring(student.student_id)}
                          sx={{ p: 0.5, fontSize: '1.2rem' }}
                          title="Stop Monitoring"
                        >
                          ⏸️
                        </IconButton>
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => blankScreen(student.student_id)}
                          sx={{ p: 0.5, fontSize: '1.2rem' }}
                          title="Blank Screen"
                        >
                          🔒
                        </IconButton>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => unblankScreen(student.student_id)}
                          sx={{ p: 0.5, fontSize: '1.2rem' }}
                          title="Unblank Screen"
                        >
                          🔓
                        </IconButton>
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleOpenUrlClick(student.student_id)}
                          sx={{ p: 0.5, fontSize: '1.2rem' }}
                          title="Open URL"
                        >
                          🌐
                        </IconButton>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Fullscreen Modal */}
      {fullscreenStudent && (
        <Modal
          open={Boolean(fullscreenStudent)}
          onClose={() => setFullscreenStudent(null)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.95)'
          }}
        >
          <Box sx={{ 
            position: 'relative', 
            width: '95vw', 
            height: '95vh',
            outline: 'none'
          }}>
            {/* Header */}
            <Box sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              p: 2, 
              bgcolor: 'rgba(0,0,0,0.7)',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: 1
            }}>
              <Box>
                <Typography variant="h5">
                  👁️ {fullscreenStudent.name || fullscreenStudent.student_id}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Form: {fullscreenStudent.form}
                </Typography>
              </Box>
              <IconButton 
                onClick={() => setFullscreenStudent(null)}
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Video */}
            <Box sx={{ 
              width: '100%', 
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <video
                id={`video-fullscreen-${fullscreenStudent.student_id}`}
                ref={(videoElement) => {
                  if (videoElement) {
                    // Copy stream from thumbnail video to fullscreen video
                    const thumbnailVideo = document.getElementById(`video-${fullscreenStudent.student_id}`);
                    if (thumbnailVideo && thumbnailVideo.srcObject) {
                      videoElement.srcObject = thumbnailVideo.srcObject;
                    }
                  }
                }}
                autoPlay
                playsInline
                muted
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>

            {/* Footer with instructions */}
            <Box sx={{ 
              position: 'absolute', 
              bottom: 0, 
              left: 0, 
              right: 0, 
              p: 2, 
              bgcolor: 'rgba(0,0,0,0.7)',
              color: 'white',
              textAlign: 'center'
            }}>
              <Typography variant="body2">
                Press ESC or click ✕ to exit fullscreen
              </Typography>
            </Box>
          </Box>
        </Modal>
      )}
    </Box>
  );
};

export default LiveMonitorTab_webrtc;
